import { z } from "zod";
import PageHeader from "@/components/PageHeader";
import FormBuilder from "@/components/forms/FormBuilder";
import PrivacyLabel from "@/components/forms/PrivacyLabel";
import { usePageMeta } from "@/hooks/usePageMeta";

const schema = z.object({
  organisation_name: z.string().min(1, "Please enter your charity or organisation name"),
  contact_name: z.string().min(1, "Please enter a contact name"),
  role: z.string().min(1, "Please enter your role"),
  email: z.string().min(1, "Please enter your email").email("Please enter a valid email address"),
  telephone: z.string().min(6, "Please enter a telephone number"),
  charity_number: z.string().optional(),
  location: z.string().min(1, "Please tell us your operating location or campaign area"),
  support_type: z.string().min(1, "Please select the type of support you need"),
  start_date: z.string().optional(),
  message: z.string().min(1, "Please tell us a little about your objectives"),
  privacy: z.boolean().refine((v) => v === true, "Please acknowledge the Privacy Notice to continue"),
  company: z.string().optional(),
});

const fields = [
  { name: "organisation_name", label: "Charity or organisation name", type: "text" },
  { name: "contact_name", label: "Contact name", type: "text", width: "half" },
  { name: "role", label: "Role", type: "text", width: "half" },
  { name: "email", label: "Email", type: "email", width: "half", autoComplete: "email" },
  { name: "telephone", label: "Telephone", type: "tel", width: "half", autoComplete: "tel" },
  { name: "charity_number", label: "Charity number", type: "text", width: "half", optional: true },
  {
    name: "location",
    label: "Operating location or campaign area",
    type: "text",
    width: "half",
  },
  {
    name: "support_type",
    label: "Type of fundraising support required",
    type: "select",
    placeholder: "Select support required",
    options: [
      "Face-to-face donor acquisition",
      "Regular-giving campaigns",
      "Campaign planning and mobilisation",
      "Fundraiser recruitment and training",
      "Field-team management",
      "Quality assurance",
      "Campaign monitoring and reporting",
      "Not sure yet",
    ],
  },
  {
    name: "start_date",
    label: "Anticipated start date",
    type: "date",
    width: "half",
    optional: true,
  },
  {
    name: "message",
    label: "Message",
    type: "textarea",
    placeholder: "Tell us about your charity, objectives and the supporters you want to reach.",
  },
  { name: "privacy", label: "Privacy acknowledgement", type: "checkbox", labelNode: <PrivacyLabel /> },
];

export default function ConsultationPage() {
  usePageMeta(
    "Book a Consultation | Amanah Promotions",
    "Book a consultation with Amanah Promotions to discuss professional, ethical face-to-face fundraising for your charity."
  );
  return (
    <>
      <PageHeader
        eyebrow="Book a Consultation"
        title="Let's talk about your fundraising"
        intro="Tell us about your charity and the fundraising support you are looking for. We aim to respond as soon as possible."
      />
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10 sm:py-20 lg:px-16">
          <FormBuilder
            fields={fields}
            schema={schema}
            endpoint="/forms/consultation"
            submitLabel="Book a Consultation"
            defaultValues={{ support_type: "", privacy: false }}
            success={{
              title: "Thank you — your request has been received",
              message:
                "We have received your consultation request and a member of the Amanah team will be in touch to discuss your fundraising objectives.",
            }}
          />
        </div>
      </section>
    </>
  );
}
